import { CheckCircle2, Circle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";
import { analytics } from "@/lib/analytics";
import { useEffect } from "react";

export interface ReadinessCheck {
  key: string;
  labelKey: string;
  met: boolean;
  linkPath: string;
  linkLabelKey: string;
}

interface Props {
  checks: ReadinessCheck[];
  onLaunch: () => void;
}

export function LabReadinessGate({ checks, onLaunch }: Props) {
  const { translate } = useTranslation();
  const navigate = useNavigate();
  const metCount = checks.filter((c) => c.met).length;
  const allMet = metCount === checks.length;

  useEffect(() => {
    analytics.track("lab.readiness_gate_viewed", { requirements_met: metCount });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex flex-col items-center justify-center h-full px-6 py-12 max-w-xl mx-auto text-center space-y-6">
      <div className="space-y-2">
        <h2 className="text-xl font-semibold text-foreground">
          {translate("lab_readiness_title")}
        </h2>
        <p className="text-sm text-muted-foreground">
          {translate("lab_readiness_subtitle")}
        </p>
      </div>

      <ul className="w-full space-y-3 text-left">
        {checks.map((check) => (
          <li
            key={check.key}
            className="flex items-start gap-3 rounded-lg border bg-card p-3"
          >
              {check.met ? (
              <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
            ) : (
              <Circle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground">
                {translate(check.labelKey)}
              </p>
              {!check.met && (
                <button
                  onClick={() => navigate(check.linkPath)}
                  className="text-xs text-primary hover:underline flex items-center gap-1 mt-0.5"
                >
                  {translate(check.linkLabelKey)}
                  <ArrowRight className="h-3 w-3" />
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>

      {allMet ? (
        <div className="space-y-3 w-full">
          <p className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
            {translate("lab_readiness_ready")}
          </p>
          <Button onClick={onLaunch} className="w-full gap-2">
            {translate("lab_readiness_launch_btn")}
          </Button>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          {translate("lab_readiness_progress", {
            met: String(metCount),
            total: String(checks.length),
          })}
        </p>
      )}
    </div>
  );
}
